import { api } from "@/lib/api";

export interface GuestOpacAvailability {
  totalCopies: number;
  availableCopies: number;
  byBranch: Array<{
    branchId: string;
    branchName: string;
    totalCopies: number;
    availableCopies: number;
    statusSummary: string;
  }>;
}

export interface GuestOpacResourceSummary {
  publicId: string;
  type: "physical" | "journal" | "digital";
  title: string;
  authors: string[];
  issn?: string | null;
  isbn?: string | null;
  coverUrl?: string | null;
  callNumber?: string | null;
  publisher?: string | null;
  publishedDate?: string | null;
  physicalDescription?: string | null;
  bindingType?: string | null;
  availability?: GuestOpacAvailability;
}

export interface GuestOpacResourceDetail extends GuestOpacResourceSummary {
  description?: string | null;
  subjects?: string[];
  series?: string | null;
  lcClassification?: string | null;
  digitalType?: string | null;
}

export interface GuestOpacSearchResponse {
  items: GuestOpacResourceSummary[];
  total: number;
  page: number;
  pageSize: number;
  nextCursor?: string;
}

export interface GuestOpacConfig {
  tenant: { slug: string; name: string };
  branding: { logoUrl?: string | null; primaryColor?: string | null };
  features: { search: boolean; digitalLibrary: boolean };
  publicOpacPath: string;
  guestOpacPath: string;
}

type CatalogType = "physical" | "digital" | "all" | "journal";

function withTenantSlug(tenantSlug?: string) {
  return tenantSlug ? { headers: { "x-tenant-slug": tenantSlug } } : {};
}

export const guestOpacApi = {
  getConfig(tenantSlug?: string) {
    return api
      .get<GuestOpacConfig>("/guest/opac/config", withTenantSlug(tenantSlug))
      .then((r) => r.data);
  },

  listCatalog(
    params: { type?: CatalogType; page?: number; pageSize?: number; cursor?: string },
    tenantSlug?: string
  ) {
    return api
      .get<GuestOpacSearchResponse>("/guest/opac/catalog", {
        params,
        ...withTenantSlug(tenantSlug),
      })
      .then((r) => r.data);
  },

  search(
    params: { q: string; type?: CatalogType; page?: number; pageSize?: number; cursor?: string },
    tenantSlug?: string
  ) {
    return api
      .get<GuestOpacSearchResponse>("/guest/opac/search", {
        params,
        ...withTenantSlug(tenantSlug),
      })
      .then((r) => r.data);
  },

  getResource(publicId: string, tenantSlug?: string) {
    return api
      .get<GuestOpacResourceDetail>(`/guest/opac/resources/${encodeURIComponent(publicId)}`, {
        ...withTenantSlug(tenantSlug),
      })
      .then((r) => r.data);
  },

  getHighlights(tenantSlug?: string) {
    return api
      .get<GuestOpacResourceSummary[]>("/guest/opac/highlights", withTenantSlug(tenantSlug))
      .then((r) => r.data);
  },

  getDigitalAccess(publicId: string, tenantSlug?: string) {
    return api
      .get<{
        fileUrl: string;
        expiresInSeconds: number;
      }>(`/guest/opac/digital-resources/${encodeURIComponent(publicId)}/access`, withTenantSlug(tenantSlug))
      .then((r) => r.data);
  },
};
